import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, CloudOff, ScanLine, Zap } from 'lucide-react'
import { useConfig } from '@/features/config/ConfigProvider'
import { WorkflowIcon } from '@/ui/WorkflowIcon'
import { Screen } from '@/ui/Screen'
import { AdminLockButton } from '@/features/admin/AdminLockButton'
import { cx } from '@/lib/cx'
import type { WorkflowPublic } from '@/lib/api-types'

export function HomeScreen() {
  const navigate = useNavigate()
  const { workflows, prefs, activeWorkflow, setActiveWorkflow, updatePrefs, loading, staleError } =
    useConfig()

  const tags = useMemo(() => {
    const set = new Set<string>()
    for (const w of workflows) for (const t of w.tags) set.add(t)
    return [...set].sort()
  }, [workflows])

  const visible = useMemo(
    () =>
      prefs.tagFilter === null
        ? workflows
        : workflows.filter((w) => w.tags.includes(prefs.tagFilter!)),
    [workflows, prefs.tagFilter],
  )

  return (
    <Screen title="ScanFlow" subtitle="Pick a workflow, then scan." action={<AdminLockButton />}>
      <button
        type="button"
        onClick={() => void navigate('/scan')}
        disabled={activeWorkflow === null}
        className="mesh flex h-40 w-full flex-col items-center justify-center gap-3 rounded-[var(--radius-hero)] text-white shadow-xl shadow-brand-600/20 transition-transform active:scale-[0.98] disabled:opacity-60"
      >
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/20 ring-1 ring-white/30">
          <WorkflowIcon name={activeWorkflow?.icon} className="h-6 w-6" />
        </span>
        <span className="text-lg font-bold">Tap to scan</span>
        <span className="max-w-[85%] truncate text-sm text-white/80">
          {activeWorkflow?.name ?? 'No workflow available'}
        </span>
      </button>

      {/* A failed refresh never replaces the list — cached workflows stay usable
          and the warning explains why they might be out of date. */}
      {staleError !== null && workflows.length > 0 && (
        <p
          role="status"
          className="mt-4 flex items-start gap-2 rounded-2xl bg-warn-50 p-3 text-sm text-warn-700"
        >
          <CloudOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Showing the last saved workflows — couldn&rsquo;t reach the server to check for changes.
        </p>
      )}

      <div className="mt-8 space-y-3">
        <h2 className="label-section">Workflows</h2>

        {tags.length > 0 && (
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            <TagChip
              label="All"
              active={prefs.tagFilter === null}
              onClick={() => updatePrefs({ tagFilter: null })}
            />
            {tags.map((tag) => (
              <TagChip
                key={tag}
                label={tag}
                active={prefs.tagFilter === tag}
                onClick={() => updatePrefs({ tagFilter: tag })}
              />
            ))}
          </div>
        )}

        {loading && workflows.length === 0 ? (
          <SkeletonList />
        ) : visible.length === 0 ? (
          <EmptyState
            hasFilter={prefs.tagFilter !== null}
            onClear={() => updatePrefs({ tagFilter: null })}
          />
        ) : (
          <ul className="space-y-2">
            {visible.map((workflow) => (
              <li key={workflow.id}>
                <WorkflowRow
                  workflow={workflow}
                  active={activeWorkflow?.id === workflow.id}
                  onSelect={() => setActiveWorkflow(workflow.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Screen>
  )
}

function TagChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'min-h-touch shrink-0 rounded-2xl border px-4 text-sm font-semibold transition-colors',
        active
          ? 'border-brand-600 bg-brand-600 text-white'
          : 'border-[var(--border-subtle)] bg-[var(--surface-raised)] text-[var(--text-secondary)]',
      )}
    >
      {label}
    </button>
  )
}

function WorkflowRow({
  workflow,
  active,
  onSelect,
}: {
  workflow: WorkflowPublic
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active}
      className={cx(
        'flex w-full items-center gap-3 rounded-[var(--radius-card)] border p-3 text-left transition-colors',
        active
          ? 'border-brand-600 bg-brand-50'
          : 'border-[var(--border-subtle)] bg-[var(--surface-raised)] hover:border-[var(--border-strong)]',
      )}
    >
      <span
        className={cx(
          'grid h-11 w-11 shrink-0 place-items-center rounded-2xl',
          active ? 'bg-brand-600 text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-muted)]',
        )}
      >
        <WorkflowIcon name={workflow.icon} className="h-5 w-5" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span
            className={cx(
              'truncate text-[0.9375rem] font-semibold',
              active && 'text-brand-700',
            )}
          >
            {workflow.name}
          </span>
          {workflow.immediateSubmit && (
            <Zap
              className="h-4 w-4 shrink-0 text-warn-500"
              aria-label="Sends immediately, no form"
            />
          )}
        </span>
        {workflow.description !== '' && (
          <span className="mt-0.5 block truncate text-sm text-[var(--text-muted)]">
            {workflow.description}
          </span>
        )}
      </span>

      {active && <Check className="h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" />}
    </button>
  )
}

function SkeletonList() {
  return (
    <ul className="space-y-2" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3"
        >
          <div className="h-11 w-11 shrink-0 animate-pulse rounded-2xl bg-[var(--surface-sunken)]" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-1/2 animate-pulse rounded bg-[var(--surface-sunken)]" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--surface-sunken)]" />
          </div>
        </li>
      ))}
    </ul>
  )
}

function EmptyState({ hasFilter, onClear }: { hasFilter: boolean; onClear: () => void }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-strong)] px-4 py-8 text-center">
      <ScanLine className="mx-auto h-8 w-8 text-[var(--text-muted)]" aria-hidden="true" />
      <p className="mt-3 text-[0.9375rem] text-[var(--text-secondary)]">
        {hasFilter ? 'No workflows match this tag.' : 'No workflows have been set up yet.'}
      </p>
      {hasFilter ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-2 min-h-touch px-4 text-[0.9375rem] font-semibold text-brand-600"
        >
          Show all
        </button>
      ) : (
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          Add one from the admin page on a computer.
        </p>
      )}
    </div>
  )
}
